import React, { useState, useMemo } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Separator } from '@/components/ui/separator';
import {
  Info,
  BookOpen,
  HelpCircle,
  Compass,
  Search,
  CheckCircle2,
  CheckCircle,
  Play,
  Clock,
  Lightbulb,
  AlertCircle,
  ArrowRight,
  FileQuestion,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { getHelpContent } from '@/config/helpContent';
import { getToursByPage } from '@/config/tours';
import { useTour } from '@/contexts/useTour';
import { usePersona } from '@/contexts/PersonaContext';
import { usePermissions } from '@/contexts/PermissionContext';
import type { UserSegment } from '@/types/product-tour';

interface HelpDrawerProps {
  open: boolean;
  onClose: () => void;
  currentPath: string;
  onNavigate: (path: string) => void;
  onReopenAfterNavigate?: () => void;
}

export function HelpDrawer({ open, onClose, currentPath, onNavigate, onReopenAfterNavigate }: HelpDrawerProps) {
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('functionality');

  const helpData = getHelpContent(currentPath);
  const { startTour, restartTour, getTourState } = useTour();
  const { currentPersona } = usePersona();
  const { canViewApprovalDashboard, canViewReport } = usePermissions();

  // Derive segment from path first (manager-only pages), then fall back to current persona.
  // This ensures tours are shown correctly even when the user hasn't toggled to manager view.
  const isManagerOnlyPath =
    currentPath.startsWith('/approvals') ||
    currentPath.startsWith('/reports') ||
    currentPath.startsWith('/team') ||
    currentPath.startsWith('/projects');
  const isAdminUser = canViewApprovalDashboard() && canViewReport('utilisation');
  const userSegment: UserSegment = isManagerOnlyPath
    ? (isAdminUser ? 'admin' : 'manager')
    : currentPersona === 'manager'
      ? 'manager'
      : 'employee';

  const pageTours = getToursByPage(currentPath, userSegment);

  const q = search.trim().toLowerCase();

  // Filtered functionality key features
  const filteredKeyFeatures = useMemo(() => {
    if (!helpData || !q) return helpData?.functionality.keyFeatures ?? [];
    return helpData.functionality.keyFeatures.filter(
      f => f.label.toLowerCase().includes(q) || f.detail.toLowerCase().includes(q)
    );
  }, [helpData, q]);

  // Filtered sections
  const filteredSections = useMemo(() => {
    if (!helpData || !q) return helpData?.functionality.sectionsAndFields ?? [];
    return helpData.functionality.sectionsAndFields.filter(
      s => s.name.toLowerCase().includes(q) || s.description.toLowerCase().includes(q)
    );
  }, [helpData, q]);

  // Filtered use cases
  const filteredUseCases = useMemo(() => {
    if (!helpData || !q) return helpData?.functionality.useCases ?? [];
    return helpData.functionality.useCases.filter(u => u.toLowerCase().includes(q));
  }, [helpData, q]);

  // Filtered guide steps
  const filteredSteps = useMemo(() => {
    if (!helpData || !q) return helpData?.userGuide.steps ?? [];
    return helpData.userGuide.steps.filter(
      s =>
        s.title.toLowerCase().includes(q) ||
        s.items.some(i => (i.text ?? '').toLowerCase().includes(q) || (i.note ?? '').toLowerCase().includes(q) || (i.tip ?? '').toLowerCase().includes(q))
    );
  }, [helpData, q]);

  // Filtered FAQ
  const filteredFaq = useMemo(() => {
    if (!helpData || !q) return helpData?.faq ?? [];
    return helpData.faq.filter(
      f => f.question.toLowerCase().includes(q) || f.answer.toLowerCase().includes(q)
    );
  }, [helpData, q]);

  // Filtered tours
  const filteredTours = useMemo(() => {
    if (!q) return pageTours;
    return pageTours.filter(
      t => t.name.toLowerCase().includes(q) || t.description.toLowerCase().includes(q)
    );
  }, [pageTours, q]);

  const handleTourClick = (tourId: string) => {
    const state = getTourState(tourId);
    onClose();
    // Small delay to let the drawer close before the tour overlay appears
    setTimeout(() => {
      if (state?.status === 'completed') {
        restartTour(tourId);
      } else {
        startTour(tourId);
      }
    }, 300);
  };

  const handleRelatedNavigate = (path: string) => {
    onClose();
    onNavigate(path);
    // Re-open the help drawer after navigation so the destination page's help is shown
    if (onReopenAfterNavigate) {
      setTimeout(onReopenAfterNavigate, 300);
    }
  };

  // Completed tours count for the tab badge
  const completedCount = pageTours.filter(t => getTourState(t.id)?.status === 'completed').length;

  if (!helpData && pageTours.length === 0) {
    // Nothing to show — render a minimal no-content drawer
    return (
      <Sheet open={open} onOpenChange={v => !v && onClose()}>
        <SheetContent side="right" className="w-full sm:max-w-[600px] p-0 flex flex-col gap-0">
          <SheetHeader className="px-6 py-5 border-b bg-muted/40">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                <FileQuestion className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h2 className="text-lg font-semibold">Help</h2>
                <p className="text-sm text-muted-foreground">No help content available for this page yet.</p>
              </div>
            </div>
          </SheetHeader>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Sheet open={open} onOpenChange={v => !v && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-[600px] p-0 flex flex-col gap-0">
        {/* Fixed header */}
        <SheetHeader className="px-6 py-4 border-b bg-muted/40 shrink-0">
          <div className="flex items-center gap-3 mb-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 shrink-0">
              {helpData?.icon
                ? React.createElement(helpData.icon, { className: 'h-5 w-5 text-primary' })
                : <FileQuestion className="h-5 w-5 text-primary" />}
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-semibold leading-tight">{helpData?.pageTitle ?? 'Help'}</h2>
              <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{helpData?.description}</p>
            </div>
          </div>
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search help content…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-8 h-9 text-sm"
            />
          </div>
        </SheetHeader>

        {/* Tabs + scrollable content */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col" style={{ flex: '1 1 0', minHeight: 0 }}>
          <TabsList className="grid grid-cols-4 rounded-none border-b h-10 shrink-0 bg-muted/20">
            <TabsTrigger value="functionality" className="text-xs gap-1 rounded-none data-[state=active]:border-b-2 data-[state=active]:border-primary">
              <Info className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Functionality</span>
            </TabsTrigger>
            <TabsTrigger value="guide" className="text-xs gap-1 rounded-none data-[state=active]:border-b-2 data-[state=active]:border-primary">
              <BookOpen className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">User Guide</span>
            </TabsTrigger>
            <TabsTrigger value="faq" className="text-xs gap-1 rounded-none data-[state=active]:border-b-2 data-[state=active]:border-primary">
              <HelpCircle className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">FAQ</span>
            </TabsTrigger>
            <TabsTrigger value="tours" className="text-xs gap-1 rounded-none data-[state=active]:border-b-2 data-[state=active]:border-primary">
              <Compass className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Tours</span>
              {pageTours.length > 0 && (
                <Badge
                  variant={completedCount === pageTours.length ? 'secondary' : 'default'}
                  className="h-4 px-1 text-[10px] leading-none"
                >
                  {completedCount}/{pageTours.length}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* ── FUNCTIONALITY TAB ── */}
          <TabsContent value="functionality" className="m-0 overflow-y-auto" style={{ flex: '1 1 0', minHeight: 0 }}>
            <div>
              <div className="px-6 py-5 space-y-6">
                {helpData && (
                  <>
                    {/* Overview */}
                    <section>
                      <h3 className="text-sm font-semibold mb-2">Overview</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">{helpData.functionality.overview}</p>
                    </section>

                    <Separator />

                    {/* Key Features */}
                    <section>
                      <h3 className="text-sm font-semibold mb-3">Key Features</h3>
                      {filteredKeyFeatures.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No features match your search.</p>
                      ) : (
                        <ul className="space-y-2.5">
                          {filteredKeyFeatures.map(f => (
                            <li key={f.label} className="flex items-start gap-2.5">
                              <CheckCircle className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                              <div>
                                <span className="text-sm font-medium">{f.label}</span>
                                <span className="text-sm text-muted-foreground"> — {f.detail}</span>
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </section>

                    <Separator />

                    {/* Sections & Fields */}
                    {helpData.functionality.sectionsAndFields.length > 0 && (
                      <>
                        <section>
                          <h3 className="text-sm font-semibold mb-3">Sections &amp; Fields</h3>
                          {filteredSections.length === 0 ? (
                            <p className="text-sm text-muted-foreground">No sections match your search.</p>
                          ) : (
                            <div className="rounded-md border overflow-hidden">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="bg-muted/50 border-b">
                                    <th className="px-3 py-2 text-left font-medium text-xs text-muted-foreground w-2/5">Section / Field</th>
                                    <th className="px-3 py-2 text-left font-medium text-xs text-muted-foreground">Description</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {filteredSections.map((s, i) => (
                                    <tr key={s.name} className={cn('border-b last:border-0', i % 2 === 0 ? 'bg-background' : 'bg-muted/20')}>
                                      <td className="px-3 py-2 font-medium align-top">{s.name}</td>
                                      <td className="px-3 py-2 text-muted-foreground align-top">{s.description}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </section>
                        <Separator />
                      </>
                    )}

                    {/* Use Cases */}
                    <section>
                      <h3 className="text-sm font-semibold mb-3">Use Cases</h3>
                      {filteredUseCases.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No use cases match your search.</p>
                      ) : (
                        <ul className="space-y-1.5">
                          {filteredUseCases.map((u, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                              <span className="mt-0.5 h-1.5 w-1.5 rounded-full bg-primary/60 shrink-0 mt-2" />
                              {u}
                            </li>
                          ))}
                        </ul>
                      )}
                    </section>
                  </>
                )}
              </div>
            </div>
          </TabsContent>

          {/* ── USER GUIDE TAB ── */}
          <TabsContent value="guide" className="m-0 overflow-y-auto" style={{ flex: '1 1 0', minHeight: 0 }}>
            <div>
              <div className="px-6 py-5 space-y-5">
                {helpData && (
                  <>
                    {/* Getting Started callout */}
                    <div className="flex items-start gap-3 rounded-md border border-blue-200 bg-blue-50 p-4 dark:border-blue-900 dark:bg-blue-950/30">
                      <Info className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-sm font-semibold text-blue-800 dark:text-blue-300 mb-1">Getting Started</p>
                        <p className="text-sm text-blue-700 dark:text-blue-400 leading-relaxed">{helpData.userGuide.gettingStarted}</p>
                      </div>
                    </div>

                    {/* Step-by-step accordion */}
                    {filteredSteps.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No steps match your search.</p>
                    ) : (
                      <Accordion type="multiple" className="space-y-2">
                        {filteredSteps.map((step, si) => (
                          <AccordionItem
                            key={si}
                            value={`step-${si}`}
                            className="border rounded-md px-4 shadow-none"
                          >
                            <AccordionTrigger className={`text-sm font-medium py-3 hover:no-underline${step.titleAlign === 'left' ? ' !justify-start' : ''}`}>
                              {step.title}
                            </AccordionTrigger>
                            <AccordionContent className="pb-4">
                              <ol className="space-y-3">
                                {step.items.map((item, ii) => (
                                  <li key={ii} className="space-y-2">
                                    {item.text && (
                                      <div className="flex items-start gap-2.5">
                                        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary mt-0.5">
                                          {ii + 1}
                                        </span>
                                        <span className="text-sm">{item.text}</span>
                                      </div>
                                    )}

                                    {/* Note callout */}
                                    {item.note && (
                                      <div className="ml-7 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 dark:border-amber-900 dark:bg-amber-950/30">
                                        <AlertCircle className="h-3.5 w-3.5 text-amber-600 mt-0.5 shrink-0" />
                                        <p className="text-xs text-amber-800 dark:text-amber-300 leading-relaxed">
                                          <span className="font-semibold">Note: </span>{item.note}
                                        </p>
                                      </div>
                                    )}

                                    {/* Tip callout */}
                                    {item.tip && (
                                      <div className="ml-7 flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 dark:border-blue-900 dark:bg-blue-950/30">
                                        <Lightbulb className="h-3.5 w-3.5 text-blue-500 mt-0.5 shrink-0" />
                                        <p className="text-xs text-blue-800 dark:text-blue-300 leading-relaxed">
                                          <span className="font-semibold">Tip: </span>{item.tip}
                                        </p>
                                      </div>
                                    )}

                                    {/* Sub-steps */}
                                    {item.subSteps && item.subSteps.length > 0 && (
                                      <ul className="ml-7 space-y-1">
                                        {item.subSteps.map((sub, si2) => (
                                          <li key={si2} className="flex items-start gap-2 text-sm text-muted-foreground">
                                            <ArrowRight className="h-3 w-3 mt-1 shrink-0" />
                                            {sub}
                                          </li>
                                        ))}
                                      </ul>
                                    )}
                                  </li>
                                ))}
                              </ol>
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    )}

                    {/* Pro Tips */}
                    {helpData.userGuide.proTips.length > 0 && (
                      <div className="rounded-md border border-amber-200 bg-amber-50 p-4 dark:border-amber-900 dark:bg-amber-950/30">
                        <div className="flex items-center gap-2 mb-3">
                          <Lightbulb className="h-4 w-4 text-amber-600 shrink-0" />
                          <h4 className="text-sm font-semibold text-amber-800 dark:text-amber-300">Pro Tips</h4>
                        </div>
                        <ul className="space-y-2">
                          {helpData.userGuide.proTips.map((tip, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-amber-800 dark:text-amber-400">
                              <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-amber-500 shrink-0" />
                              {tip}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </TabsContent>

          {/* ── FAQ TAB ── */}
          <TabsContent value="faq" className="m-0 overflow-y-auto" style={{ flex: '1 1 0', minHeight: 0 }}>
            <div>
              <div className="px-6 py-5">
                {filteredFaq.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    {q ? 'No FAQ items match your search.' : 'No FAQ available for this page yet.'}
                  </p>
                ) : (
                  <Accordion type="multiple" className="space-y-2">
                    {filteredFaq.map((item, i) => (
                      <AccordionItem
                        key={i}
                        value={`faq-${i}`}
                        className="border rounded-md px-4 shadow-none"
                      >
                        <AccordionTrigger className="text-sm font-medium py-3 hover:no-underline text-left">
                          {item.question}
                        </AccordionTrigger>
                        <AccordionContent className="pb-4 text-sm text-muted-foreground">
                          <p className="leading-relaxed">{item.answer}</p>
                          {item.steps && item.steps.length > 0 && (
                            <ol className="mt-3 space-y-1.5">
                              {item.steps.map((step, si) => (
                                <li key={si} className="flex items-start gap-2.5">
                                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                                    {si + 1}
                                  </span>
                                  <span>{step}</span>
                                </li>
                              ))}
                            </ol>
                          )}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                )}
              </div>
            </div>
          </TabsContent>

          {/* ── GUIDED TOURS TAB ── */}
          <TabsContent value="tours" className="m-0 overflow-y-auto" style={{ flex: '1 1 0', minHeight: 0 }}>
            <div>
              <div className="px-6 py-5 space-y-3">
                {filteredTours.length === 0 ? (
                  <div className="text-center py-10 text-muted-foreground">
                    <Compass className="h-8 w-8 mx-auto mb-3 opacity-30" />
                    <p className="text-sm font-medium">No guided tours available for this page yet.</p>
                    <p className="text-xs mt-1">Check back as new tours are added over time.</p>
                  </div>
                ) : (
                  filteredTours.map(tour => {
                    const state = getTourState(tour.id);
                    const isCompleted = state?.status === 'completed';
                    const isInProgress = state?.status === 'in_progress';

                    return (
                      <div
                        key={tour.id}
                        className="rounded-lg border bg-card p-4 flex items-start gap-3"
                      >
                        <div className="shrink-0 mt-0.5">
                          {isCompleted ? (
                            <CheckCircle2 className="h-5 w-5 text-green-600" />
                          ) : (
                            <Play className="h-5 w-5 text-primary" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span className="text-sm font-medium">{tour.name}</span>
                            {isCompleted && (
                              <Badge variant="secondary" className="text-xs">Completed</Badge>
                            )}
                            {isInProgress && (
                              <Badge variant="default" className="text-xs">In Progress</Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground leading-relaxed mb-3">{tour.description}</p>
                          <div className="flex items-center gap-3">
                            <Button
                              size="sm"
                              variant={isCompleted ? 'outline' : 'default'}
                              className="h-7 text-xs gap-1.5"
                              onClick={() => handleTourClick(tour.id)}
                            >
                              <Play className="h-3 w-3" />
                              {isCompleted ? 'Restart Tour' : isInProgress ? 'Continue Tour' : 'Start Tour'}
                            </Button>
                            {tour.estimatedDuration && (
                              <span className="flex items-center gap-1 text-xs text-muted-foreground">
                                <Clock className="h-3 w-3" />
                                ~{tour.estimatedDuration}s
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Fixed footer */}
        {helpData && helpData.relatedPages.length > 0 && (
          <div className="px-6 py-4 border-t bg-muted/40 shrink-0">
            <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">Related Pages</p>
            <div className="flex flex-wrap gap-2">
              {helpData.relatedPages.map(page => (
                <Button
                  key={page.path}
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs gap-1"
                  onClick={() => handleRelatedNavigate(page.path)}
                >
                  <ArrowRight className="h-3 w-3" />
                  {page.label}
                </Button>
              ))}
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
